import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "app/popleads/components/ui-kit/Card";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { POPUP_CONTENTS } from "../utils/constants";
import { useExtensionContext } from "../ExtensionContext";
import ButtonWithLabel from "app/popleads/components/ui-kit/ButtonWithLabel";
import styled from "styled-components";
import { toast } from "react-toastify";
import { colors } from "app/popleads/components/ui-kit/colors";
import { StyledCardContent } from "./common/styled";
import ExtensionNote from "./ExtensionNote";
import RichTextEditor from "app/popleads/components/ui-kit/RichTextEditor";
import { useDropzone } from "react-dropzone";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { decideFileType } from "../../activities/utils";
import SVG from "react-inlinesvg";
import useProfileNoteActions from "app/popleads/hooks/useProfileNoteActions";
import { ProfileLoadingOverlay } from "../ExtensionHome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import removeTrailingEmptyNodes from "../utils/removeTrailingEmptyNodes";
import { post } from "app/popleads/networking/RequestService";
import MaterialIcon from "app/popleads/components/ui-kit/MaterialIcon";
import { apiErrorText } from "app/popleads/networking/apiErrorText";

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const TitleWithAction = styled.div`
    color: ${colors.neutral[600]};
    display: block;
`;

const CreateNoteContainer = styled.div`
    margin-top: 8px;
`;

const CreateNoteActionsContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    justify-content: end;
`;

const CreateNoteFileItemsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0;
    padding: 1px 8px;
    box-sizing: border-box;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 9px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${colors.neutral[500]};
        border-radius: 9px;
        border: 3px solid transparent;
        background-clip: content-box;
        min-height: 32px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.neutral[700]};
    }

    &::-webkit-scrollbar-corner {
        background-color: ${colors.neutral[100]};
    }
`;

const CreateNoteFileItem = styled.div`
    display: flex;
    height: 23px;
    align-items: center;
    gap: 4px;
    user-select: none;
`;

const FileIconWrapper = styled.div`
    width: 20px;
    height: 20px;
    min-width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    & > span,
    & > svg {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const FileNameText = styled.span`
    color: #8b999f;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    cursor: default;
`;

const StyledButtonWithText = styled(ButtonWithText)`
    span > span {
        font-size: 20px !important;
    }
`;

const LoadMoreContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px;
    min-height: 40px;
`;

const ROWS_PER_PAGE = 10;

const ExtensionNoteCard = ({ showFilesAndAttachments, setShowFilesAndAttachments }) => {
    const {
        setState,
        profileNotesRefetch,
        noteList,
        setNoteList,
        profile,
        popit,
        collapsedCards,
        setCollapsedCard,
        profileNotesLoading,
        noteRef,
        companyUsers,
        refetchAttachments,
    } = useExtensionContext();
    const collapsed = collapsedCards.note;
    const handleCollapseChange = (val) => {
        setCollapsedCard("note", val);
    };
    const [noteListArr, setNoteListArr] = useState([]);
    const [isCreatingNote, setIsCreatingNote] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const { createNote } = useProfileNoteActions();
    const formRef = useRef(null);

    // Infinite scroll state
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const currentStartRef = useRef(ROWS_PER_PAGE);
    const isFetchingMoreRef = useRef(false);
    const profileId = profile?.id;

    // Reset pagination when profile changes
    useEffect(() => {
        currentStartRef.current = ROWS_PER_PAGE;
        setHasMore(true);
        setIsLoadingMore(false);
    }, [profileId]);

    // Load more notes function
    const loadMoreNotes = useCallback(async () => {
        if (isFetchingMoreRef.current || !hasMore || profileNotesLoading || !profileId || collapsed) return;

        isFetchingMoreRef.current = true;
        setIsLoadingMore(true);
        try {
            const startPayload = currentStartRef.current;
            const res = await post({
                fn: "profileNoteList",
                profileId: profileId,
                start: startPayload,
                rowsPerPage: ROWS_PER_PAGE,
            });

            const newNotes = res.data?.profileNotes || [];

            if (newNotes.length > 0) {
                setNoteList((prev) => [...prev, ...newNotes]);
                currentStartRef.current = startPayload + ROWS_PER_PAGE;

                if (newNotes.length < ROWS_PER_PAGE) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            //console.error("Error loading more notes:", error);
        } finally {
            isFetchingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [isLoadingMore, hasMore, profileNotesLoading, profileId, collapsed, setNoteList]);

    // Scroll-based infinite scroll detection
    useEffect(() => {
        const scrollContainer = document.getElementById("profile-container");
        if (!scrollContainer || collapsed) return;

        const handleScroll = () => {
            if (isFetchingMoreRef.current || !hasMore || profileNotesLoading) return;

            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
            const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 100;

            if (scrolledToBottom) {
                loadMoreNotes();
            }
        };

        scrollContainer.addEventListener("scroll", handleScroll);

        return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
        };
    }, [loadMoreNotes, isLoadingMore, hasMore, collapsed, profileNotesLoading]);

    const refetchProfileNotes = (rowsPerPage) => {
        // Reset pagination state when refetching
        currentStartRef.current = rowsPerPage || ROWS_PER_PAGE;
        setHasMore(true);
        profileNotesRefetch({ rowsPerPage: rowsPerPage, start: 0 });
    };

    const handleAttachmentsClick = () => {
        if (popit) {
            toast.warning("You need to POP the profile first.");
        } else {
            setState({ popupContent: POPUP_CONTENTS.NOTES, popupIsOpen: true });
            setShowFilesAndAttachments(true);
            refetchProfileNotes(100);
        }
    };

    const handleNotesOpen = () => {
        // if (popit) {
        //     toast.warning("You need to POP the profile first.");
        // } else {
        //     setState({ popupContent: POPUP_CONTENTS.NOTES, popupIsOpen: true });
        //     setShowFilesAndAttachments(false);
        // }
    };

    const handleAddNoteClick = () => {
        if (popit) {
            toast.warning("You need to POP the profile first.");
        } else {
            setIsCreatingNote((prev) => !prev);
            if (!isCreatingNote && collapsedCards.note === true) {
                setCollapsedCard("note", false);
            }
        }
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/plain": [".txt"],
            "video/mp4": [".mp4"],
            "text/csv": [".csv"],
            "image/svg+xml": [".svg"],
            "application/zip": [".zip"],
            "application/x-rar-compressed": [".rar"],
        },
        onDrop: (acceptedFiles) => {
            setSelectedFiles((prevFiles) =>
                [...prevFiles, ...acceptedFiles].map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );
        },
        onDropRejected: () => {
            toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
        },
    });

    function uploadPlugin(editor) {
        editor.editing.view.document.on("clipboardInput", (event, data) => {
            const files = Array.from(data.dataTransfer.files).filter((file) => {
                const ext = file.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                return (
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/png") ||
                    file.type.startsWith("application/pdf") ||
                    file.type.startsWith("application/msword") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
                    file.type.startsWith("application/vnd.ms-excel") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
                    file.type.startsWith("text/plain") ||
                    file.type.startsWith("video/mp4") ||
                    file.type.startsWith("text/csv") ||
                    file.type.startsWith("image/svg+xml") ||
                    file.type.startsWith("application/zip") ||
                    file.type.startsWith("application/x-zip-compressed") ||
                    file.type.startsWith("application/x-rar-compressed") ||
                    (file.type.startsWith("application/x-compressed") && (ext === "zip" || ext === "rar"))
                );
            });
            if (files.length !== data.dataTransfer.files.length) {
                toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
            }
            if (files.length > 0) {
                event.stop();
                setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
            }
        });
    }

    const mentionsFeed = companyUsers?.map((user) => {
        return {
            userId: user.id,
            text: user.name || user.ad,
            id: `@${user.name || user.ad}`,
        };
    });

    const editorConfig = {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
        placeholder: selectedFiles.length > 0 ? "" : "Type your note (*Activate mention by typing '@')",
        extraPlugins: [uploadPlugin],
        mention: {
            feeds: [
                {
                    marker: "@",
                    feed: mentionsFeed || [],
                    minimumCharacters: 1,
                },
            ],
        },
        link: {
            addTargetToExternalLinks: true,
        },
    };

    const getUserIdByName = (userName) => {
        if (!userName || !companyUsers) return null;
        const cleanName = userName.replace("@", "");
        const user = companyUsers.find(
            (u) => (u.name || u.ad) === cleanName || (u.name || u.ad)?.toLowerCase() === cleanName.toLowerCase()
        );

        return user?.id || null;
    };

    const extractMentionIds = (text) => {
        try {
            const mentionIds = [];

            try {
                const dataRegex = /data-mention="([^"]+)"/g;
                let dataMatch;
                while ((dataMatch = dataRegex.exec(text)) !== null) {
                    const mentionText = dataMatch[1];
                    const userId = getUserIdByName(mentionText);

                    if (userId && !mentionIds.includes(userId.toString())) {
                        mentionIds.push(userId.toString());
                    }
                }
            } catch (e) {}

            if (mentionIds.length === 0) {
                try {
                    const userNames = companyUsers?.map((user) => user.name || user.ad).filter(Boolean) || [];
                    userNames.forEach((userName) => {
                        if (text.includes(`@${userName}`)) {
                            const user = companyUsers.find((u) => (u.name || u.ad) === userName);
                            if (user && user.id && !mentionIds.includes(user.id.toString())) {
                                mentionIds.push(user.id.toString());
                            }
                        }
                    });
                } catch (e) {}
            }

            if (mentionIds.length === 0) {
                try {
                    const mentionParser = new DOMParser();
                    const doc = mentionParser.parseFromString(text, "text/html");

                    const mentionSpans = doc.querySelectorAll(".mention");
                    mentionSpans.forEach((span) => {
                        const userName = span.textContent?.replace("@", "");
                        if (userName) {
                            const userId = getUserIdByName("@" + userName);
                            if (userId && !mentionIds.includes(userId.toString())) {
                                mentionIds.push(userId.toString());
                            }
                        }
                    });

                    if (mentionIds.length === 0) {
                        const textContent = doc.body.textContent || "";
                        const mentionMatches = textContent.match(/@(\w+)/g);

                        if (mentionMatches && mentionMatches.length > 0) {
                            mentionMatches.forEach((match) => {
                                const userId = getUserIdByName(match);
                                if (userId && !mentionIds.includes(userId.toString())) {
                                    mentionIds.push(userId.toString());
                                }
                            });
                        }
                    }
                } catch (e) {}
            }

            return mentionIds;
        } catch (error) {
            return [];
        }
    };

    const handleCreateNoteSubmit = async () => {
        try {
            setLoading(true);

            const editorHtml = removeTrailingEmptyNodes(newNote);
            const mentionIds = extractMentionIds(editorHtml);

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = editorHtml;
            const plainText = tempDiv.innerHTML || tempDiv.textContent || tempDiv.innerText || "";

            let sendNoteObj = {
                profil: profile.id,
                not: plainText,
                isNotification: 0,
            };
            //mention id gönder
            if (mentionIds.length > 0) {
                sendNoteObj["mentionIds"] = mentionIds;
            }

            if (selectedFiles && selectedFiles.length > 0) {
                const inputnames = [];
                selectedFiles.forEach((file, index) => {
                    if (file && file.name) {
                        inputnames.push(`note${index}`);
                        sendNoteObj[`note${index}`] = file;
                    }
                });
                if (inputnames.length > 0) {
                    sendNoteObj.inputName = inputnames;
                }
            }

            const result = await createNote(sendNoteObj);

            if (result.error) return;

            setNewNote("");
            setSelectedFiles([]);
            setIsCreatingNote(false);

            if (selectedFiles.length > 0) {
                refetchAttachments();
            }

            refetchProfileNotes(10);
        } catch (error) {
            toast.error(apiErrorText(error));
        } finally {
            setLoading(false);
        }
    };

    const handleEditorReady = (editor) => {
        editor.editing.view.focus();
        editor.editing.view.document.on("keydown", (event, data) => {
            if (data.keyCode === 13) {
                if (!data.shiftKey) {
                    data.preventDefault();
                    if (formRef.current) {
                        formRef.current.dispatchEvent(new Event("submit", { cancelable: true }));
                    }
                } else {
                    const selection = editor.model.document.selection;
                    const currentPosition = selection.getLastPosition();
                    const currentBlock = currentPosition.parent;

                    if (currentBlock.name === "listItem" && currentBlock.isEmpty) {
                        editor.model.change((writer) => {
                            const paragraph = writer.createElement("paragraph");
                            writer.insert(paragraph, currentBlock, "after");
                            writer.setSelection(paragraph, "in");
                            data.domEvent.preventDefault();
                        });
                        const editable = editor.ui.view.editable.element;
                        editable.scrollTop = editable.scrollHeight;
                        return;
                    }
                    if (
                        editor.model.document.selection.anchor.parent.name === "listItem" ||
                        editor.model.document.selection.anchor.parent.name === "paragraph"
                    ) {
                        editor.execute("enter");
                    }
                    const editable = editor.ui.view.editable.element;
                    editable.scrollTop = editable.scrollHeight;
                    data.domEvent.preventDefault();
                }
            }
        });
    };

    useEffect(() => {
        if (noteList && noteList?.length > 0) {
            const newNoteList = noteList?.map((note) => {
                return {
                    id: note.id,
                    icon: note.tip === "otomatik" && (
                        <StyledCardContent variant={"warning"}>
                            <MaterialIcon icon="sync_problem" style={{ fontSize: "22px", fontWeight: "300" }} />
                        </StyledCardContent>
                    ),
                    content: <ExtensionNote variant="profile" note={note} />,
                };
            });

            setNoteListArr(newNoteList);
        } else {
            setNoteListArr([]);
        }
    }, [noteList]);

    const createNoteComponent = isCreatingNote && (
        <CreateNoteContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {loading && (
                <ProfileLoadingOverlay>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </ProfileLoadingOverlay>
            )}

            <form ref={formRef} onSubmit={handleCreateNoteSubmit}>
                <RichTextEditor
                    variant="l3rnTech"
                    minHeight={"82px"}
                    maxHeight={"130px"}
                    data={newNote}
                    config={editorConfig}
                    onChange={(event, editor) => {
                        try {
                            const editorData = editor.getData();
                            setNewNote(editorData);
                        } catch (error) {}
                    }}
                    onReady={(editor) => {
                        try {
                            handleEditorReady(editor);
                        } catch (error) {}
                    }}
                />
            </form>
            {selectedFiles.length > 0 && (
                <CreateNoteFileItemsWrapper>
                    {selectedFiles.map((file, index) => (
                        <CreateNoteFileItem key={index}>
                            <FileIconWrapper>
                                <SVG src={toAbsoluteUrl(decideFileType(file?.name))} />
                            </FileIconWrapper>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tooltip-${index}`}>{file.name}</Tooltip>}
                            >
                                <FileNameText>{file.name}</FileNameText>
                            </OverlayTrigger>
                            <MaterialIcon
                                icon="close"
                                style={{ fontSize: "20px", cursor: "pointer", color: "#8b999f" }}
                                onClick={() => {
                                    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                }}
                            />
                        </CreateNoteFileItem>
                    ))}
                </CreateNoteFileItemsWrapper>
            )}
            <CreateNoteActionsContainer>
                <ButtonWithText
                    onClick={() => {
                        setNewNote("");
                        setSelectedFiles([]);
                        setIsCreatingNote(false);
                    }}
                    variant="danger"
                    buttonStyle="borderless"
                    size="small"
                >
                    Cancel
                </ButtonWithText>
                <ButtonWithText
                    variant={newNote?.length > 0 ? "l3rnTech" : "passive"}
                    buttonStyle="default"
                    size="small"
                    onClick={() => {
                        if (newNote?.length > 0) handleCreateNoteSubmit();
                    }}
                >
                    Save
                </ButtonWithText>
                <StyledButtonWithText
                    icon="attach_file"
                    iconType="material"
                    variant="neutral"
                    buttonStyle={"borderless"}
                    onClick={open}
                />
            </CreateNoteActionsContainer>
        </CreateNoteContainer>
    );

    return (
        <div
            ref={noteRef}
            style={{ padding: "12px 0px", borderBottom: "1px solid rgb(225, 225, 225)", position: "relative" }}
        >
            {profileNotesLoading && noteList.length > 0 && (
                <ProfileLoadingOverlay>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </ProfileLoadingOverlay>
            )}
            <Card
                isLoading={profileNotesLoading && noteList.length === 0}
                homePage={true}
                collapsed={collapsed}
                onCollapseChange={handleCollapseChange}
                variant={"notes-card"}
                button={
                    <ButtonWithText
                        label={isCreatingNote ? "Cancel new note" : "Add note"}
                        icon={isCreatingNote ? "remove" : "add"}
                        onClick={handleAddNoteClick}
                        iconType="material"
                        variant="l3rnTech"
                        buttonStyle="default"
                        size="circle"
                    />
                }
                cardTitle={
                    <TitleContainer>
                        <TitleWithAction onClick={() => handleNotesOpen()}>Notes</TitleWithAction>
                        {!profileNotesLoading && (
                            <ButtonWithLabel
                                icon="attach_file"
                                iconType="material"
                                variant="l3rnTech"
                                buttonStyle="borderless"
                                label={`${profile?.attachments?.length || 0} Attached Files`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAttachmentsClick();
                                }}
                                hover={false}
                            />
                        )}
                    </TitleContainer>
                }
                cardItems={noteListArr}
                viewMoreOnClick={() => handleNotesOpen()}
                errorDescription={
                    profileNotesLoading
                        ? `${profileNotesLoading} Loading notes... `
                        : `${profileNotesLoading} Click the + to create a note.`
                }
                iconWidth={"0px"}
                haveViewMore={false}
                component={createNoteComponent}
            />
            {/* Infinite scroll loading indicator */}
            {!collapsed && isLoadingMore && (
                <LoadMoreContainer>
                    <Spinner size={16} thickness={3} variant="l3rnTech" />
                </LoadMoreContainer>
            )}
        </div>
    );
};

export default ExtensionNoteCard;
